import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { showErrorToast } from "@/lib/error-handling";
import {
  createSavedView,
  CreateSavedViewRequest,
  deleteSavedView,
  fetchSavedViews,
  updateSavedView,
  UpdateSavedViewRequest,
} from "@/services/frontendServices/savedViewsServices";
import type { SavedViewDto } from "@/types";
import type { SavedViewGridKey } from "@/entities/SavedView";

export const savedViewKeys = {
  all: ["savedViews"] as const,
  list: (gridKey: SavedViewGridKey) =>
    [...savedViewKeys.all, "list", gridKey] as const,
};

export const useSavedViews = (gridKey: SavedViewGridKey) => {
  return useQuery<SavedViewDto[]>({
    queryKey: savedViewKeys.list(gridKey),
    queryFn: () => fetchSavedViews(gridKey),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useCreateSavedView = (gridKey: SavedViewGridKey) => {
  const queryClient = useQueryClient();
  return useMutation<SavedViewDto, Error, CreateSavedViewRequest>({
    mutationFn: (data) => createSavedView(data),
    onSuccess: (created) => {
      queryClient.setQueryData<SavedViewDto[]>(
        savedViewKeys.list(gridKey),
        (old = []) => [...old, created]
      );
      toast.success(`View "${created.name}" saved`, { duration: 3000 });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Could not save view",
        defaultMessage: "Failed to save view",
        duration: 4000,
      });
    },
  });
};

export const useUpdateSavedView = (gridKey: SavedViewGridKey) => {
  const queryClient = useQueryClient();
  return useMutation<
    SavedViewDto,
    Error,
    { id: string; patch: UpdateSavedViewRequest }
  >({
    mutationFn: ({ id, patch }) => updateSavedView(id, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData<SavedViewDto[]>(
        savedViewKeys.list(gridKey),
        (old = []) => old.map((v) => (v.id === updated.id ? updated : v))
      );
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Could not update view",
        defaultMessage: "Failed to update view",
        duration: 4000,
      });
    },
  });
};

export const useDeleteSavedView = (gridKey: SavedViewGridKey) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => deleteSavedView(id),
    onSuccess: (_, { id }) => {
      queryClient.setQueryData<SavedViewDto[]>(
        savedViewKeys.list(gridKey),
        (old = []) => old.filter((v) => v.id !== id)
      );
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Could not delete view",
        defaultMessage: "Failed to delete view",
        duration: 4000,
      });
    },
  });
};
